import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { FileText, ArrowRight, Bell } from "lucide-react";
import api, { fmtDate } from "@/lib/api";

/**
 * Dashboard banner that appears when clients have uploaded statements the
 * accountant hasn't yet acknowledged. Pulses yellow, shows a preview list of
 * up to 3 uploads, and CTAs to the Clients page (where the review modal
 * lives). Polls every 20s so uploads made during a session appear
 * automatically.
 */
export default function NewDocumentsAlert() {
  const [pending, setPending] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/statements/pending-review");
      setPending(data.pending || []);
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, 20000);
    return () => clearInterval(iv);
  }, [load]);

  if (pending.length === 0) return null;

  const preview = pending.slice(0, 3);
  const remaining = pending.length - preview.length;

  return (
    <div
      className="card-surface p-5 mb-6 border-l-4 border-l-yellow-400 relative overflow-hidden"
      data-testid="new-documents-alert"
    >
      <div className="flex items-start gap-3">
        <div className="relative shrink-0">
          <div className="w-9 h-9 rounded-lg bg-yellow-100 text-yellow-700 flex items-center justify-center">
            <Bell className="w-4 h-4" strokeWidth={1.75} />
          </div>
          <span className="absolute -top-0.5 -right-0.5 flex w-3 h-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-yellow-400 border-2 border-white" />
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="overline mb-0.5 text-yellow-700">Action needed</div>
          <div className="font-heading text-lg font-medium" data-testid="alert-headline">
            New Documents Arrived
            <span className="ml-2 text-yellow-700 num">({pending.length})</span>
          </div>
          <p className="text-sm text-ink-light mt-1">
            Your client{pending.length > 1 ? "s have" : " has"} uploaded new bank statements. Review and mark them as received.
          </p>
          <ul className="mt-3 space-y-1.5" data-testid="alert-preview-list">
            {preview.map((p) => (
              <li key={p.id} className="flex items-center gap-2 text-sm">
                <FileText className="w-3.5 h-3.5 text-yellow-700 shrink-0" strokeWidth={1.75} />
                <span className="truncate font-medium">{p.filename}</span>
                <span className="text-xs text-ink-lighter num shrink-0">· {fmtDate(p.uploaded_at)}</span>
              </li>
            ))}
            {remaining > 0 && (
              <li className="text-xs text-ink-lighter pl-5">and {remaining} more…</li>
            )}
          </ul>
          <Link
            to="/app/clients"
            className="mt-4 btn-forest inline-flex items-center gap-1.5 text-sm"
            data-testid="alert-review-cta"
          >
            Review documents
            <ArrowRight className="w-4 h-4" strokeWidth={2} />
          </Link>
        </div>
      </div>
    </div>
  );
}
